import { useCallback, useEffect } from 'react';
import { ADDRESS_CHANGED, CHAIN_CHANGED } from '@manifoldxyz/frontend-provider-types';
import { detectManifoldEthereumProvider } from '@manifoldxyz/frontend-provider-utils';
import { Network } from '@manifoldxyz/js-ts-utils';
import { UNAUTHENTICATED } from '@/common/constants';
import { TxStepId } from '@/common/types';
import { moveToStep } from '@/lib/transactionsFlow/utils';
import useMainStore from '@/store/mainStore';
import { useAnalytics } from './useAnalytics';

/**
 * Initializes the ManifoldEthereumProvider just one time
 * Handles the logic for getting delegate addresses
 * Handles the logic for getting the wallet address
 * Handles the logic for skipping for the first txStep if the wallet address is set
 */
export function useInitializeProviderOnce() {
  const store = useMainStore();
  const analytics = useAnalytics();

  const {
    txSteps,
    walletAddress,
    studioInstance,
    setWalletAddress,
    setConnectedNetwork,
    setDelegateAddresses,
    setSelectedDelegateAddress,
  } = store;

  const handleUnauthenticated = useCallback(() => {
    // dev: clear out the store of stale data
    setWalletAddress(undefined);
    setConnectedNetwork(undefined);
    setDelegateAddresses([]);
    setSelectedDelegateAddress('');
  }, [setWalletAddress, setConnectedNetwork, setDelegateAddresses, setSelectedDelegateAddress]);

  /**
   * Handles the logic for when the network is changed
   * it updates the connected network in the store
   */
  const handleNetworkChanged = useCallback(() => {
    const newNetwork = window.ManifoldEthereumProvider.chainId();
    if (newNetwork) {
      setConnectedNetwork(newNetwork);
    } else {
      setConnectedNetwork(undefined);
    }
  }, [setConnectedNetwork]);

  /**
   * this handles the logic for when the wallet address is changed
   * it updates the wallet address in the store
   * it also refreshes the delegate addresses
   * it also updates the tx steps to skip the 1st step
   */
  const handleAddressChanged = useCallback(async () => {
    const newAddress = window.ManifoldEthereumProvider.selectedAddress();

    setWalletAddress(newAddress);
    if (studioInstance && newAddress) {
      // Identify the user in analytics
      analytics?.identify({
        distinctId: newAddress.toLowerCase(),
        properties: {
          user_wallet: newAddress.toLowerCase(),
        },
      });
    } else if (!newAddress) {
      // disconnected wallet
      // handles resetting the tx steps to the initial state
      handleUnauthenticated();
    }

    handleNetworkChanged();
  }, [setWalletAddress, analytics, studioInstance, handleUnauthenticated, handleNetworkChanged]);

  useEffect(() => {
    const connectWalletStepIndex = txSteps.findIndex((step) => step.config.id === TxStepId.Connect);
    if (connectWalletStepIndex === -1) {
      return;
    }
    /**
     * @important
     * If wallet address was changed, we need to set the current step to the step after the connect step to start over
     * If wallet address was not changed, we need to set the current step to the connect step so user can first connect their wallet
     */

    if (walletAddress && studioInstance) {
      moveToStep(store, connectWalletStepIndex + 1);
    } else {
      moveToStep(store, connectWalletStepIndex);
    }
  }, [walletAddress, studioInstance]);

  // This useEffect ensures a ManifoldEthereumProvider is available throughout
  // the app lifecycle
  useEffect(
    () => {
      const initialize = async () => {
        // Wait for a window.ManifoldEthereumProvider to be available
        // do not wait for another component to initialize it
        await detectManifoldEthereumProvider({ initialized: false });
        if (window.ManifoldEthereumProvider && !window.ManifoldEthereumProvider.initialized) {
          // If the ManifoldEthereumProvider is not initialized, initialize it
          // Note: to match with m-connect within the other pages, which is configured
          // to support relay by allowing all networks, we do not pass the network if we are on mainnet
          await window.ManifoldEthereumProvider.initialize({
            network: Network.isTestnet(studioInstance?.publicData.network ?? 0)
              ? studioInstance?.publicData.network
              : undefined,
            browserProviderTimeout: 500,
            browserProviderIgnoreDisconnect: true,
          });
        }
      };
      initialize();
    },
    // @dev: the empty deps array ensures this only runs once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Event listener effect
  useEffect(() => {
    window.addEventListener(UNAUTHENTICATED, handleUnauthenticated);
    window.addEventListener(ADDRESS_CHANGED, handleAddressChanged);
    window.addEventListener(CHAIN_CHANGED, handleNetworkChanged);
    return () => {
      window.removeEventListener(ADDRESS_CHANGED, handleAddressChanged);
      window.removeEventListener(UNAUTHENTICATED, handleUnauthenticated);
      window.removeEventListener(CHAIN_CHANGED, handleNetworkChanged);
    };
  }, [handleUnauthenticated, handleAddressChanged, handleNetworkChanged]);
}
